// What a mark IS on the disc: the elements the circle draws for a vehicle, an
// event or a district name, and the merge that makes one badge of several
// (SPEC-redesign §5.6, §6.2). Nothing here knows about the camera, the radius
// or the closure that mounts the wheel — every one of these reads only its
// arguments, so #217 (which mark says what) and #243 (how a mark is graded)
// both live here rather than inside `mountWheel`.
import type { Item } from '../shared/model.ts';
import type { ViewState } from '../state/viewstate.ts';
import { viewState } from '../state/viewstate.ts';
import { badgeLabel, countedNoun, markName, tagLabel } from './marks.ts';
import { type Anchor, clusterAnchors, clusterCountLabel } from './wheel-geometry.ts';

/**
 * How many times the badges are re-measured and merged again (#177).
 *
 * Merging makes the badge that stays WIDER — „174" becomes „174 ×2", 37,3 px
 * to 55,9 px — so the one that stayed can land on a third badge that was clear
 * of both. The room it gains is HORIZONTAL, so only a badge to the side of it
 * can be reached: one up the meridian is 22 px tall whatever its label says.
 *
 * A second round is reachable and is asserted (`a badge that widened by merging
 * then reaches a third vehicle`); the cap above two is here so a paint cannot
 * loop, not because three rounds were ever needed.
 */
const MERGE_ROUNDS = 3;
/** Every badge is one voice to the merge: two lines in one place are one smudge. */
const BADGE_VOICE = 'badge';

/**
 * A drawn badge while the merge is being settled: its anchor on the ground, the
 * box that was drawn for it, and the vehicles it stands for. `holds` is the one
 * the click handler reads, so a badge that takes another one over answers for
 * both without being rebuilt.
 */
export type Badge = {
  anchor: HTMLElement;
  el: HTMLElement;
  holds: Item[];
  dimmed: boolean;
  x: number;
  y: number;
};

/**
 * Badges whose boxes land on one another become one badge with a count —
 * „174 ×2" — exactly as the tags do (#177). Measured rather than derived: a
 * badge's width is its line number's, so the boxes are read off the page
 * after they are drawn and the merge is redone until none of them touch.
 *
 * Which badge STAYS, in this order: the OPEN one, then one inside the line
 * lens, then the first of the cluster in axis order — the late vehicle and
 * then the nearest (§4.5).
 *
 * The open one first, because the badge speaks for the group: it carries the
 * line number and the accessible name, and that is all the wheel says. With
 * the axis deciding, opening „Tramwaj 12" from the rail lit a badge reading
 * „5 pojazdów w tym miejscu. Autobus 174, Unii Lubelskiej", pressed — the one
 * thing a screen reader had, and false. An open vehicle is never absorbed;
 * its badge reads its own line and its own name with the count beside it.
 *
 * Then the lens, because „tylko 16" would otherwise step the resident's own
 * vehicle back behind a 174 it merged with and say nothing at all. Its
 * dimming is the badge's; the count is still every vehicle standing there.
 *
 * Returns the anchor the spoke should point at when a merge changed which
 * badge answers for the open vehicle.
 */
export function mergeBadges(badges: Badge[], view: ViewState): Badge | null {
  let selected: Badge | null = null;
  for (let round = 0; round < MERGE_ROUNDS; round++) {
    const live = badges.filter((badge) => badge.anchor.isConnected);
    const anchors: Anchor<Badge>[] = live.map((badge) => {
      const drawn = badge.el.getBoundingClientRect();
      return { item: badge, voice: BADGE_VOICE, x: badge.x, y: badge.y, w: drawn.width, h: drawn.height };
    });
    const clusters = clusterAnchors(anchors);
    if (clusters.length === anchors.length) break;
    for (const cluster of clusters) {
      if (cluster.members.length < 2) continue;
      const stays =
        cluster.members.find((member) => member.holds.some((item) => item.id === view.selected)) ??
        cluster.members.find((member) => !member.dimmed) ??
        cluster.item;
      for (const member of cluster.members) {
        if (member === stays) continue;
        // One badge per PLACE now; the rail still has one row per vehicle.
        // The one that stays keeps its own vehicle at the head of what it
        // holds, so a tap opens what the badge says it is.
        stays.holds.push(...member.holds);
        member.anchor.remove();
      }
      nameMark(stays.el, stays.holds);
      // The merged badge reads selected when any vehicle it holds is open,
      // and the spoke has to agree: the badge that was merged away is never
      // drawn again, so a spoke to it would point at nothing.
      if (stays.holds.some((item) => item.id === view.selected)) {
        stays.el.classList.add('is-selected');
        // The anchor too: the ladder that puts a selected badge above the
        // centre of the circle is read there, not on what is drawn inside it.
        stays.anchor.classList.add('is-selected');
        stays.el.setAttribute('aria-pressed', 'true');
        selected = stays;
      }
    }
  }
  return selected;
}

/**
 * How far a district name may step off its anchor to find a free place, as a
 * share of its own box. A name still has to sit over the ground it names, so
 * this is a nudge and never a search of the disc.
 */
const DISTRICT_STEP = 1.1;

/**
 * How much clear space a name keeps around it.
 *
 * Boxes that merely fail to intersect are not apart: measured on the live feed
 * at 5 km, „WILDA" sat 5,0 px above „ŁĘGI DĘBIŃSKIE" and over the same ground,
 * and the two read as one word — which is the case §5.2 names in those very
 * words. Dropping the name was hiding it; letting the ladder place it needs
 * this, or the ladder puts it somewhere it cannot be read.
 */
const DISTRICT_CLEARANCE = 6;

/**
 * A district's name at the first of its anchors that is free, or null when it
 * has none. Each candidate is pulled inside the block in its own right, so a
 * name that steps sideways cannot step off the edge to do it.
 *
 * The order is the ladder's own, written down in §6.2: the anchor itself, then
 * above and below it, then either side — a step of 1,1 × the box each way. A
 * name that has to move is still a name over its own ground; a name that is
 * dropped orients nobody, which is what the default 3 km view was showing.
 * (§5.2 carries the HINT's three-place ladder, which is a different one.)
 */
export function placeDistrict(
  el: HTMLElement,
  at: { x: number; y: number },
  block: DOMRect,
  taken: DOMRect[],
): DOMRect | null {
  const box = el.getBoundingClientRect();
  const dx = box.width * DISTRICT_STEP;
  const dy = box.height * DISTRICT_STEP;
  for (const [ox, oy] of [
    [0, 0],
    [0, -dy],
    [0, dy],
    [-dx, 0],
    [dx, 0],
  ] as const) {
    if (!pullInside(el, { x: at.x + ox, y: at.y + oy }, block)) continue;
    const label = el.getBoundingClientRect();
    const near = DISTRICT_CLEARANCE;
    const clash = taken.some(
      (m) =>
        label.left - near < m.right &&
        label.right + near > m.left &&
        label.top - near < m.bottom &&
        label.bottom + near > m.top,
    );
    if (!clash) return label;
  }
  return null;
}

/**
 * Places a district's name at its anchor, pulling it toward the centre of the
 * block until the whole box is inside it. Returns false when even the centre
 * cannot hold it — a name wider than the block, which the 72 px dial would give.
 *
 * Since Decision 46 the boundary is the block's rectangle, not a rim: there is
 * no circle to be cut in half by any more, and what „GRUNWALD" must not run off
 * is the edge of the screen. Pulling along the line to the centre keeps the
 * name over the ground it belongs to; moving it any other way would put
 * „WILDA" over Łazarz.
 */
function pullInside(el: HTMLElement, at: { x: number; y: number }, block: DOMRect): boolean {
  const fits = (box: DOMRect) =>
    box.left >= block.left && box.right <= block.right && box.top >= block.top && box.bottom <= block.bottom;

  const cx = block.width / 2;
  const cy = block.height / 2;
  for (let pull = 0; pull <= 1.001; pull += 0.1) {
    const x = cx + (at.x - cx) * (1 - pull);
    const y = cy + (at.y - cy) * (1 - pull);
    el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    if (fits(el.getBoundingClientRect())) return true;
  }
  return false;
}

const SVG = 'http://www.w3.org/2000/svg';

/** The crosshair on the recentre button — a glyph, drawn, not an icon file. */
export function crosshair(): SVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  const ring = document.createElementNS(SVG, 'circle');
  ring.setAttribute('cx', '12');
  ring.setAttribute('cy', '12');
  ring.setAttribute('r', '5');
  const ticks = document.createElementNS(SVG, 'path');
  ticks.setAttribute('d', 'M12 2v4M12 18v4M2 12h4M18 12h4');
  svg.append(ring, ticks);
  return svg;
}

/**
 * Other vehicles open nothing, so they are decoration: no name, no tab stop,
 * and not a button at all (D-f, §8). The prototype puts eight focusable no-op
 * buttons before any content; this is what replaces them.
 */
export function dotElement(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'circle__dot';
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/**
 * A mark that has a row is `tabindex="-1"`: a pointer target, with the row as
 * the keyboard path (D-f). Opening from a mark and opening from a row are the
 * same call, and the highlight is mutual because both read `sel` (§4.7).
 */
/**
 * What one mark says and what it counts: „PL 3", „174 ×2", or the plain label
 * when it stands for one thing. The count comes first in the accessible name,
 * so a screen reader says how many there are before it says which one opens.
 *
 * A badge keeps its line number and wears the count beside it, because the
 * number is the fact a resident reads at a glance; a tag has no number of its
 * own, so the count takes that place, exactly as it already did.
 */
function nameMark(el: HTMLElement, holds: Item[]): void {
  const item = holds[0];
  if (!item) return;
  const count = holds.length;
  el.setAttribute(
    'aria-label',
    count > 1
      ? `${count} ${clusterCountLabel(count, ...countedNoun(item))} w tym miejscu. ${markName(item)}`
      : markName(item),
  );
  if (item.kind === 'vehicle') {
    const number = el.firstElementChild;
    if (number) number.textContent = badgeNumber(holds);
    return;
  }
  el.textContent = count > 1 ? `${tagLabel(item)} ${count}` : tagLabel(item);
}

/**
 * What a merged badge shows, and the one place the two forms are written down.
 *
 * `×` counts vehicles OF THAT LINE, so it may only be used where every vehicle
 * in the place is on it: „174 ×2" over a 174 and a 16 is a statement about the
 * city that a resident can see is false. Where the lines differ the badge names
 * the one it stands for and counts the rest — „16 +1" — which says „and others
 * here" without claiming they are 16s. The accessible name carries the whole
 * group either way, and the rail carries a row for every one of them.
 */
export function badgeNumber(holds: Item[]): string {
  const item = holds[0];
  if (!item) return '';
  const line = badgeLabel(item);
  if (holds.length === 1) return line;
  return holds.every((held) => badgeLabel(held) === line)
    ? `${line} ×${holds.length}`
    : `${line} +${holds.length - 1}`;
}

export function markButton(item: Item, selected: boolean, dimmed: boolean, holds: Item[]): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.tabIndex = -1;
  el.className = item.kind === 'vehicle' ? 'circle__badge' : 'circle__tag';
  // Square for a tram, a pill for a bus, read from `mode` and never from the
  // glyph: `▢` and `◯` are how the kind looks, not what it is (§4.4).
  if (item.kind === 'vehicle') {
    el.classList.add(item.mode === 'tram' ? 'circle__badge--tram' : 'circle__badge--bus');
  }
  el.classList.toggle('is-selected', selected);
  el.classList.toggle('is-dimmed', dimmed);
  // §4.4 grades every item — started .75, stale .7, early .6, older than 48 h
  // .55 — and the prototype compounds that with the lens rather than letting
  // one win: .75 outside the lens is 0,3375, not 0,75.
  //
  // The weight is handed to the stylesheet as a number and multiplied there.
  // An inline `opacity` would beat the lens's class outright, which is how the
  // two came apart and ran the fade backwards — an older item drawn brighter
  // than a fresh one. There is no inline opacity here on purpose.
  el.style.setProperty('--w', String(item.weight));
  // We do not trust this position, and the mark says so without a colour (§7.2).
  el.classList.toggle('is-uncertain', item.uncertain);
  el.setAttribute('aria-pressed', String(selected));

  if (item.kind === 'vehicle') {
    el.append(document.createElement('span'));
    // The heading arrow, only when the feed gave one — a mark that points
    // nowhere is worse than a mark that does not point (§4.4).
    if (item.heading != null) {
      const arrow = document.createElement('span');
      arrow.className = 'circle__heading';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↑';
      arrow.style.rotate = `${item.heading}deg`;
      el.append(arrow);
    }
  }
  nameMark(el, holds);

  el.addEventListener('click', () => {
    // The prototype toggles, and `aria-pressed` has to mean something: a pressed
    // state that will not unpress is worse than none. Opening is an opening, so
    // it pushes; closing goes through `closeDetail()`, which is the one closing
    // gesture and never leaves an entry Back could re-open it from (§4.2, D-1).
    //
    // What is open is read at the tap and not captured at the paint: a badge
    // that took another badge over after this handler was made answers for both
    // of them, so a captured boolean would have it re-open what is already open.
    // The one it opens is the first it holds, which is the late vehicle and then
    // the nearest — the one the axis puts at the top of the group.
    const open = viewState.get().selected;
    const first = holds[0] ?? item;
    if (holds.some((held) => held.id === open)) viewState.closeDetail();
    else viewState.set({ selected: first.id }, 'open');
  });
  return el;
}
